import type { ReactNode } from "react";
import { useMeta } from "@/api/queries";
import { BrandMark } from "@/components/BrandMark";
import { formatStamp } from "@/print/stamp";
import { useBrand } from "@/theme/context";

/**
 * The printed page's chrome: letterhead, as-of stamp, footer (PRD FR-9.1, FR-11.1).
 *
 * Every part of it comes out of the theme. A rebrand — name, logo, accent,
 * footer copy — reaches the PDF because the PDF is this page, printed. There
 * is no second set of letterhead constants to keep in step.
 *
 * The stamp is not decoration. A PDF is the one surface where nobody can press
 * refresh, so "as of" has to be on it, in the deployment timezone, before
 * anybody circulates the numbers.
 */
export function PrintLayout({
  title,
  subtitle,
  asOf,
  facts,
  children,
}: {
  title: string;
  subtitle?: string;
  /** ISO timestamp of the values on the page, or null when nothing says. */
  asOf?: string | null;
  facts?: Array<[string, string]>;
  children: ReactNode;
}) {
  const { productName, tagline, footer } = useBrand();
  const { data: meta } = useMeta();
  const stamp = formatStamp(asOf, meta?.locale ?? "en-US", meta?.timezone);

  return (
    <div className="print-page bg-bg text-ink mx-auto w-full max-w-[52rem] px-8 py-8">
      <header className="border-border mb-6 flex items-start gap-3 border-b pb-4">
        <BrandMark size={34} />
        <div className="min-w-0 flex-1">
          <p className="type-caption text-accent">{productName}</p>
          <h1 className="type-h1 text-ink mt-1 leading-tight">{title}</h1>
          {subtitle ? (
            <p className="type-support text-ink-secondary mt-1.5">{subtitle}</p>
          ) : null}
        </div>
        <div className="type-caption text-ink-secondary shrink-0 text-end normal-case">
          <p>As of</p>
          <p className="text-ink">{stamp}</p>
        </div>
      </header>

      {facts?.length ? (
        <dl className="type-support text-ink-secondary mb-6 flex flex-wrap gap-x-5 gap-y-1">
          {facts.map(([label, value]) => (
            <div key={label} className="flex gap-1.5">
              <dt className="text-ink-disabled">{label}</dt>
              <dd className="text-ink">{value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {children}

      {/* The footer the branding page edits ("Email and PDF footer"), the same
          sentence the email and the Markdown export print — then the name and
          the moment. It used to print the tagline instead, which on a rebrand
          that left the tagline alone was the platform's own. */}
      <footer className="border-border type-caption text-ink-disabled mt-10 border-t pt-3 normal-case">
        {footer ? <p>{footer}</p> : null}
        <p>
          {productName}
          {tagline ? ` — ${tagline}` : ""} · {stamp}
        </p>
      </footer>
    </div>
  );
}

/** A section heading on a printed page, kept with the block under it. */
export function PrintSection({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="print-block mb-5">
      {title ? <h2 className="type-h3 text-ink mb-2">{title}</h2> : null}
      {children}
    </section>
  );
}

/** The optional "Queries used" appendix (PRD FR-9.1, FR-5.4). */
export function SqlAppendix({
  queries,
}: {
  queries: Array<{ index: number; sql: string; connection?: string; rows?: number | null }>;
}) {
  if (!queries.length) return null;
  return (
    <section className="border-border mt-8 border-t pt-4">
      <h2 className="type-h3 text-ink mb-3">Queries used</h2>
      {queries.map((query) => (
        <div key={`${query.index}-${query.sql.slice(0, 24)}`} className="print-block mb-3">
          <p className="type-caption text-ink-disabled mb-1 normal-case">
            Query {query.index}
            {query.connection ? ` · ${query.connection}` : ""}
            {typeof query.rows === "number" ? ` · ${query.rows} rows` : ""}
          </p>
          <pre className="bg-surface border-border rounded-element type-code text-ink overflow-hidden border px-3 py-2 whitespace-pre-wrap">
            {query.sql.trim()}
          </pre>
        </div>
      ))}
    </section>
  );
}
